import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronDownIcon, StarIcon, ShieldCheckIcon, TrendingUpIcon, ShoppingBagIcon } from 'lucide-react';
export function HeroSection() {
  const scrollToNext = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-32 pb-20" data-id="element-109">
      {/* Premium SaaS Background */}
      <div className="absolute inset-0 bg-[#020202]" data-id="element-110">
        {/* Subtle grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]" style={{
        maskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)'
      }} data-id="element-111" />

        {/* Glows */}
        <div className="absolute top-[-10%] left-[20%] w-[40rem] h-[40rem] bg-[#D4AF37]/10 rounded-full blur-[120px] opacity-50 mix-blend-screen" data-id="element-112" />
        <div className="absolute top-[10%] right-[20%] w-[30rem] h-[30rem] bg-emerald-500/10 rounded-full blur-[120px] opacity-40 mix-blend-screen" data-id="element-113" />

        {/* Noise texture */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-15 mix-blend-overlay pointer-events-none" data-id="element-114" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full flex flex-col items-center text-center" data-id="element-115">
        {/* Eyebrow Badge */}
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 backdrop-blur-md mb-10 hover:bg-white/[0.05] transition-colors cursor-default" data-id="element-116">
          <div className="flex items-center gap-1.5" data-id="element-117">
            <ShieldCheckIcon className="w-4 h-4 text-emerald-400" data-id="element-118" />
            <span className="text-xs font-semibold text-emerald-300 tracking-wide uppercase" data-id="element-119">
              Shopify Partner
            </span>
          </div>
          <div className="w-1 h-1 rounded-full bg-white/20" data-id="element-120" />
          <div className="flex items-center gap-1.5" data-id="element-121">
            <StarIcon className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" data-id="element-122" />
            <span className="text-xs font-semibold text-[#F3E5AB] tracking-wide uppercase" data-id="element-123">
              Top Rated on Fiverr
            </span>
          </div>
        </div>

        {/* Headline */}
        <h1 className="text-6xl md:text-8xl lg:text-[7rem] font-bold tracking-tighter mb-8 leading-[1.05] text-white" data-id="element-124">
          Elite Shopify
          <br data-id="element-125" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-300 to-gray-500" data-id="element-126">
            Development.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed font-medium" data-id="element-127">
          We build high-converting stores, premium themes, and powerful apps for
          ambitious ecommerce brands.{' '}
          <span className="text-gray-300" data-id="element-128">
            Not just web designers, but Shopify specialists.
          </span>
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center gap-5 w-full sm:w-auto mb-24" data-id="element-129">
          <Link to="/contact" className="w-full sm:w-auto bg-white text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center justify-center gap-2 group hover:scale-105 shadow-[0_0_40px_rgba(255,255,255,0.1)]" data-id="element-130">
            Start Your Project
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-131" />
          </Link>
          <Link to="/portfolio" className="w-full sm:w-auto px-8 py-4 rounded-full font-bold text-lg text-white border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-md transition-all duration-300 flex items-center justify-center hover:scale-105" data-id="element-132">
            View Portfolio
          </Link>
        </div>

        {/* Authority / Stats Band */}
        <div className="w-full max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 pt-10 border-t border-white/10" data-id="element-133">
          <div className="flex flex-col items-center justify-center p-4" data-id="element-134">
            <div className="flex items-center gap-2 mb-2" data-id="element-135">
              <ShoppingBagIcon className="w-5 h-5 text-emerald-400" data-id="element-136" />
              <span className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-137">
                1,000+
              </span>
            </div>
            <span className="text-sm text-gray-500 font-medium uppercase tracking-wider" data-id="element-138">
              Stores Built
            </span>
          </div>

          <div className="flex flex-col items-center justify-center p-4" data-id="element-139">
            <div className="flex items-center gap-2 mb-2" data-id="element-140">
              <StarIcon className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" data-id="element-141" />
              <span className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-142">
                700+
              </span>
            </div>
            <span className="text-sm text-gray-500 font-medium uppercase tracking-wider" data-id="element-143">
              5-Star Reviews
            </span>
          </div>

          <div className="flex flex-col items-center justify-center p-4" data-id="element-144">
            <div className="flex items-center gap-2 mb-2" data-id="element-145">
              <TrendingUpIcon className="w-5 h-5 text-blue-400" data-id="element-146" />
              <span className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-147">
                100M+
              </span>
            </div>
            <span className="text-sm text-gray-500 font-medium uppercase tracking-wider" data-id="element-148">
              Client Revenue
            </span>
          </div>

          <div className="flex flex-col items-center justify-center p-4" data-id="element-149">
            <div className="flex items-center gap-2 mb-2" data-id="element-150">
              <ShieldCheckIcon className="w-5 h-5 text-purple-400" data-id="element-151" />
              <span className="text-3xl md:text-4xl font-bold text-white tracking-tight" data-id="element-152">
                100%
              </span>
            </div>
            <span className="text-sm text-gray-500 font-medium uppercase tracking-wider" data-id="element-153">
              Shopify Focused
            </span>
          </div>
        </div>
      </div>

      <button onClick={scrollToNext} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-600 hover:text-white transition-colors animate-bounce" aria-label="Scroll down" data-id="element-154">
        <ChevronDownIcon className="w-6 h-6" data-id="element-155" />
      </button>
    </section>;
}